'use strict';

import { fh_polygon } from '@acenv/fh-3d-viewer';
import { cn_building } from '../../model/cn_building';
import { cn_scene } from '../../model/cn_scene';
import { cn_storey } from '../../model/cn_storey';
import { cn_vertex } from '../../model/cn_vertex';
import { cn_wall } from '../../model/cn_wall';
import { cn_wall_type } from '../../model/cn_wall_type';
import { cn_add, cn_clone, cn_dist, cn_dot, cn_intersect_line, cn_middle, cn_mul, cn_normal, cn_normalize, cn_sub, cnx_clone } from '../cn_utilities';
import { cn_opening_type } from '../../model/cn_opening_type';
import { cn_opening } from '../../model/cn_opening';
import { cn_material } from '../../model/cn_material';
import { logger } from '../cn_logger';

//***********************************************************************************
//***********************************************************************************
//**** WISEBIM importer
//***********************************************************************************
//***********************************************************************************

export function cn_read_bim(wisebim_json) {
    var reader = new cn_bim_reader();
    reader.parse(wisebim_json);
    return reader._building;
}

export class cn_bim_reader {
    constructor() {
        this._building = null;
    }

    //***********************************************************************************
    //**** Main function
    //***********************************************************************************
    parse(bim_json) {
        this._building = new cn_building();
        this._building.storeys = [];

        this._wall_types = [];
        bim_json.storeys.forEach(s => this._read_storey(s));
    }

    /**
     * read a storey
     * @param {any} wb_storey
     */
    _read_storey(wb_storey) {
        if (!wb_storey.walls) return;
        const storey = new cn_storey(this._building);
        storey.storey_index = this._building.storeys.length;
        this._building.storeys.push(storey);
        //storey.ID = wb_storey.id;
        storey.name = wb_storey.name;

        const scene = new cn_scene(this._building);
        storey.scene = scene;
        scene.storey = storey;

        let wall_names = [];
        wb_storey.walls.forEach(wb_wall => {
            if (wall_names.includes(wb_wall.name)) return;
            wall_names.push(wb_wall.name);
            const p = [wb_wall.point0, wb_wall.point1]
            var thickness = wb_wall.thickness;

            var vertices = [null, null];
            for (var nv = 0; nv < 2; nv++) {
                const pp = p[nv];
                const vertex = new cn_vertex(pp, false, scene);
                scene.vertices.push(vertex);
                vertices[nv] = vertex;
            }
            var wall_type = this._building.get_wall_types().find(wt => Math.abs(wt.thickness - thickness) < 0.005);
            if (!wall_type) {
                wall_type = new cn_wall_type();
                wall_type.name = 'Mur ' + (0.1 * Math.round(thickness * 1000)).toFixed(1) + 'cm';
                wall_type.layers = [cn_material.material_by_code(this._building, 'unknown', thickness)];
                this._building.element_types.push(wall_type);
            }

            const wall = new cn_wall(vertices[0], vertices[1], wall_type, 0, scene);
            wall["name"] = wb_wall.name;
            wall["original"] = wb_wall;
            scene.insert_wall(wall);
        });

        scene.walls.sort((a,b) => b.wall_type.thickness - a.wall_type.thickness);

        this._merge_close_vertices(scene);

        this._continue_supported_vertices(scene);

        let opening_names = [];
        wb_storey.walls.forEach(wb_wall => {
            if (!wb_wall.openings) return;
            let wall = scene.walls.find(w => w["name"] == wb_wall.name);
            if (!wall) return;
            wb_wall.openings.forEach(op => {
                if (opening_names.includes(op.name)) return;
                opening_names.push(op.name);
                this._read_opening(wall,op);
             });

        });
        scene.run_diagnostic();

    }

    _merge_close_vertices(scene) {
        scene.vertices.forEach(v => v.walls = []);
        scene.walls.forEach(w =>{
            w.vertices.forEach(v => v.walls.push(w));
        });

        scene.walls.forEach((wall,wi) => {
            let threshold = wall.wall_type.thickness*1.5;
            let d0 = cn_sub(wall.vertices[1].position,wall.vertices[0].position);
            cn_normalize(d0);
            wall.vertices.forEach(vertex => {
                if (vertex.walls.length>1) return;
                let p0 = vertex.position;
                for (var i=wi+1;i<scene.walls.length;i++) {
                    let other_wall = scene.walls[i];
                    let d1 = cn_sub(other_wall.vertices[1].position,other_wall.vertices[0].position);
                    cn_normalize(d1);
                    for (var vi=0;vi<2;vi++) {
                        let other_vertex = other_wall.vertices[vi];
                        if (other_vertex.walls.length > 1) continue;
                        let inters = cn_intersect_line(p0,d0,other_vertex.position,d1);
                        if (!inters) continue;
                        if (cn_dist(p0,inters) > threshold) continue;
                        if (cn_dist(other_vertex.position,inters) > threshold) continue;
                        vertex.position = inters;
                        vertex.walls.push(other_wall);
                        other_wall.vertices[vi] = vertex;
                        break;
                    }
                    if (vertex.walls.length > 1) break;
                }
            });
        });
    }
    
    _continue_supported_vertices(scene) {
       
        scene.vertices.forEach(v => v.walls = []);
        scene.walls.forEach(w =>{
            w.vertices.forEach(v => v.walls.push(w));
            w.build_self();
        });

        scene.vertices.filter(vertex => vertex.walls.length == 1).forEach(vertex => {
            let p0 = vertex.position;
            let d0 = vertex.walls[0].direction_to(vertex);
            let threshold = vertex.walls[0].wall_type.thickness*1.5;
            scene.walls.find(wall => {
                let pscal = cn_dot(wall.bounds.normal,d0);
                if (Math.abs(pscal) < 0.01) return false;
                let p1 = (pscal>0)?wall.bounds.pmin:wall.bounds.pmax;
                let t = cn_dot(cn_sub(p1,p0),wall.bounds.normal) / pscal;
                if (t < 0 || t > threshold) return false;
                let pos = cn_add(p0,cn_mul(d0,t));
                let x = cn_dot(wall.bounds.direction,cn_sub(pos,p1));
                if (x<0 || x > wall.bounds.length) return false;
                vertex.position = pos;
                vertex.walls[0].build_self();
                return true;
            });
        });

    }
    _read_opening(wall, opening) {
        let p0 = wall.vertices[0].position;
        let dir = cn_sub(wall.vertices[1].position,wall.vertices[0].position);
        cn_normalize(dir);
        let x0 = cn_dot(dir,cn_sub(opening.point0,p0));
        let x1 = cn_dot(dir,cn_sub(opening.point1,p0));
        let x = Math.min(x0,x1);
        x = cn_dot(dir,cn_sub(cn_middle(opening.point1,opening.point0),p0));
        let opening_type = (opening.z>0)?this._building.get_window_types()[0]:this._building.get_door_types()[0];

        let op = new cn_opening(opening_type);
        op.position = x;
        op.wall = wall;
        op.width = Math.abs(x1-x0);
        op.height = opening.height;
        op.z = opening.z;
        op["name"] = opening.name;
        op["original"] = opening;
        console.log("opening placement : ", x, opening,wall["original"]);
        wall.openings.push(op);
    }

}


